import { useIsChuseokCallOff } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import {
  // BTypeCardView,
  buildCrematoriumPageSchema,
  call,
  ColorIcon,
  colors,
  CTypeCardView,
  customEvent,
  DTypeBreadCrumb,
  DTypeCardView,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  CrematoriumRegionFAQ,
  GOI_WEB_URL,
  HLine,
  RichSearch,
  SidoName,
} from "goi_common";

import { Box, Button, Stack, VStack } from "@chakra-ui/react";

import crematoriums from "goi_common/src/constants/hwajang_detail.json";
import { GetStaticPaths, GetStaticProps } from "next";

type CrematoriumDetail = (typeof crematoriums)[number];

import Head from "next/head";

const { targetSidos, regionColor } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const sidoList = Array.from(FUNERAL_SIDO.entries()).filter(([code]) =>
    targetSidos.includes(code.toString())
  );

  const paths = sidoList
    .map((sido) => {
      const sigungus = FUNERAL_SIGUNGU.get(sido[0]);

      if (sigungus) {
        return Array.from(sigungus.values()).map((sigungu) => {
          return {
            params: {
              sido: sido[1],
              sigungu,
            },
          };
        });
      }

      return [];
    })
    .filter((i) => i != null);

  return {
    paths: paths.flat(),
    fallback: false,
  };
};
export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { sido, sigungu } = params as { sido: string; sigungu: string };

  const sidoCode = Array.from(FUNERAL_SIDO.entries()).find(
    ([, value]) => value === SidoName[sido as keyof typeof SidoName]
  )?.[0];

  const sigunguCode = Array.from(FUNERAL_SIGUNGU.values())
    .flatMap((m) => Array.from(m.entries()))
    .find(([, name]) => name === sigungu)?.[0];

  /** 시군구까지 일치하는 것을 앞으로 배치, 시도일치를 뒤로 배치 */
  const regionCrematoriums = crematoriums
    .filter((c) => targetSidos.includes(c.sido.toString()))
    .sort((a, b) => {
      // 시도 일치 여부 비교 (1순위)
      const sidoCompare =
        Number(b.sido === Number(sidoCode)) -
        Number(a.sido === Number(sidoCode));

      if (sidoCompare !== 0) return sidoCompare;

      // 시군구 일치 여부 비교 (2순위)
      return (
        Number(b.sigungu === Number(sigunguCode)) -
        Number(a.sigungu === Number(sigunguCode))
      );
    });

  const district =
    sigungu.length >= 3 && (sigungu.endsWith("시") || sigungu.endsWith("구"))
      ? sigungu.slice(0, -1)
      : sigungu;

  const regionText = `${sido} ${district}`;

  const crematoriumItemDescriptions = regionCrematoriums.map(
    (crematorium) => `${crematorium.companyname} ${crematorium.address}`
  );

  const title = `(${regionText}) 화장터(화장장) 추천, 비용, 예약, 비교 정보 | 서울경기인천장례정보포털`;
  const description = `(${regionText}) 내 화장장(화장터), 총 ${
    regionCrematoriums?.length
  }개의 ${sigungu} 및 인접 화장장(화장터) 종류, 추천, 예약 및 비용 정보 알아보기. 주변 화장장과 비교하기. ${
    crematoriumItemDescriptions.length > 0
      ? crematoriumItemDescriptions.join(", ")
      : ""
  }`;

  const keywords = `${regionText} 화장장, ${sido} 화장장, ${sigungu} 화장장, ${district} 화장장, 
${regionText} 화장터, ${sido} 화장터, ${sigungu} 화장터, ${district} 화장터, 
${regionText} 화장장 추천, ${sido} 화장장 추천, ${sigungu} 화장장 추천, ${district} 화장장 추천, 
${regionText} 화장장 정보, ${sido} 화장장 정보, ${sigungu} 화장장 정보, ${district} 화장장 정보, 
${regionText} 화장장 비교, ${sido} 화장장 비교, ${sigungu} 화장장 비교, ${district} 화장장 비교, 
${regionText} 화장장 예약, ${sido} 화장장 예약, ${sigungu} 화장장 예약, ${district} 화장장 예약,
${regionText} 화장장 비용, ${sido} 화장장 비용, ${sigungu} 화장장 비용, ${district} 화장장 비용,
${regionText} 화장터 추천, ${sido} 화장터 추천, ${sigungu} 화장터 추천, ${district} 화장터 추천, 
${regionText} 화장터 정보, ${sido} 화장터 정보, ${sigungu} 화장터 정보, ${district} 화장터 정보, 
${regionText} 화장터 비교, ${sido} 화장터 비교, ${sigungu} 화장터 비교, ${district} 화장터 비교, 
${regionText} 화장터 예약, ${sido} 화장터 예약, ${sigungu} 화장터 예약, ${district} 화장터 예약,
${regionText} 화장터 비용, ${sido} 화장터 비용, ${sigungu} 화장터 비용, ${district} 화장터 비용,
${sido} 화장예약, ${sido} 화장 비용, 
${sigungu} 화장예약, ${sigungu} 화장 비용,
${regionText} 화장예약, ${regionText} 화장 비용,
${district} 화장예약, ${district} 화장 비용,
${regionCrematoriums
  ?.map((c) => {
    const name =
      c.companyname.includes("화장장") || c.companyname.includes("화장터")
        ? c.companyname
        : `${c.companyname} 화장장`;
    return `${name}, ${name} 화장비용, ${name} 화장예약, ${name} 서류, ${name} 시간, ${name} 시설, ${name} 유택동산, ${name} 근처 화장장, ${name} 근처 화장터`;
  })
  .join(", ")}, 화장장, 화장터, 화장예약, 화장 비용, 화장지원금`;

  return {
    props: {
      sidoCode,
      sidoName: sido,
      sigunguCode,
      sigunguName: sigungu,
      district,
      regionCrematoriums,
      pageDescription: description,
      pageKeywords: keywords,
      pageOgTitle: title,
      pageOgDescription: description,
      pageOgImage: regionCrematoriums[0]?.images[0]?.url,
    },
  };
};

function CrematoriumRegionSigunguPage({
  sidoName,
  sigunguName,
  district,
  regionCrematoriums,
  pageOgTitle,
}: {
  sidoName: string;
  sidoCode: number;
  sigunguCode: number;
  sigunguName: string;
  district: string;
  regionCrematoriums: CrematoriumDetail[];
  pageOgTitle: string;
}) {
  const isChuseokCallOff = useIsChuseokCallOff();
  const defaultCrematorium = regionCrematoriums[0];
  const totalCrematoriums = regionCrematoriums.length;

  const CrematoriumRegionHeroSection = () => {
    return (
      <VStack
        width="100%"
        style={{
          background: regionColor.c100,
          // padding: "40px 20px",
        }}
      >
        <img
          src={defaultCrematorium?.images[0]?.url}
          alt={`자세한 ${defaultCrematorium?.companyname} 시설 사진`}
          width="100%"
          height="100%"
        />
      </VStack>
    );
  };

  const regionText = `${sidoName} ${district}`;

  const crematoriumPageSchema = buildCrematoriumPageSchema(regionCrematoriums, {
    regionText,
    goiWebUrl: GOI_WEB_URL,
  });

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
      </Head>
      <RichSearch data={crematoriumPageSchema} />

      <main>
        <section style={{ padding: "20px 20px", textAlign: "center" }}>
          <DTypeBreadCrumb
            mainColor={regionColor}
            firstItem={{
              text: `${sidoName} 화장장`,
              href: "",
            }}
            secondItem={{
              text: `${sigunguName} 화장장`,
              href: ``,
            }}
          />
          <Box margin="0px 0px 20px 0px">
            <CrematoriumRegionHeroSection />
          </Box>

          <h1
            className="head_24_b"
            style={
              {
                // marginBottom: "10px",
              }
            }
          >
            {regionText} 화장장(화장터) 추천
            <br />및 비용, 예약 정보
          </h1>
          <p
            className="body_14_m gray700"
            style={{
              margin: "8px",
            }}
          >
            {regionText} 화장장(화장터) 및 인접 지역의 화장장을 통틀어 총{" "}
            {regionCrematoriums?.length}개의 화장장(화장터) 중에서
            <br /> 지역, 비용, 시설별 추천 화장장(화장터)을 확인해보세요.
            {/* {sidoName} {sigunguName}의 대표 화장장 {defaultCrematorium?.companyname} */}
          </p>
        </section>

        <Box padding="0px 15px">
          <HLine height="1px" color={colors.gray900} />
        </Box>

        <section
          style={{
            // background: regionColor.c100,
            padding: "20px 34px",
          }}
        >
          <VStack>
            <p
              className="subtitle_16_b gray900"
              style={{
                marginBottom: "10px",
                textAlign: "center",
              }}
            >
              {regionText} 주소지 기준으로 관내/관외 적용 여부 및
              <br />
              화장터 비용, 예약 가능 여부 확인
            </p>

            <Box className="body_14_m gray900"></Box>

            <VStack width="100%">
              {/* 2026 추석. 연휴에는 못 받는 전화라 이 버튼만 내린다. */}
              {!isChuseokCallOff && (
                <Button
                  backgroundColor={regionColor.c400}
                  border="none"
                  width="100%"
                  borderRadius="8px"
                  padding="16px"
                  cursor="pointer"
                  onClick={() => {
                    customEvent({
                      domain: "metro",
                      path: "crematorium",
                      trigger_and_target: "goi_call",
                    });

                    call("1666-9784");
                  }}
                >
                  <VStack gap="0px">
                    {/* <Box className="caption_12_m white">
                    예약, 비용, 시설 이용 안내
                    </Box> */}

                    <Box className="subtitle_16_b white">
                      화장장 정보, 비용, 예약 문의
                    </Box>
                  </VStack>
                </Button>
              )}
              <Stack flexDirection="row" gap="6px" alignItems="center">
                <ColorIcon name="icon-check-fill-mono" size={14} color="" />
                <Box className="caption_12_m">{`${regionText} 기반으로 알아보기`}</Box>
              </Stack>
            </VStack>
          </VStack>
        </section>

        <Box padding="0px 15px">
          <HLine height="1px" color={colors.gray900} />
        </Box>

        <section style={{ padding: "20px 16px", marginTop: "20px" }}>
          <VStack
            width="100%"
            gap="5px"
            alignItems="flex-start"
            padding="0px 0px 10px 0px"
          >
            <h2 className="subtitle_18_sb gray900">
              {regionText} 화장장(화장터) 및 인근 화장시설
            </h2>
            <p className="body_14_m gray700">
              {regionText} 인접 지역의 화장장 예약이 가능한 {totalCrematoriums}
              개의 화장장(화장터)이 존재합니다.
            </p>
          </VStack>

          {/* <DTypeCardView */}

          <VStack as="ul" width="100%" gap="16px">
            {regionCrematoriums.map((crematorium) => (
              <li key={crematorium?.id} style={{ width: "100%" }}>
                <CTypeCardView
                  sido={sidoName}
                  sigungu={sigunguName}
                  mainColor={regionColor}
                  // address={crematorium?.address}
                  descriptions={[crematorium?.address]}
                  // arrowText="자세히 알아보기"
                  href={`${GOI_WEB_URL}/crematorium-recommendation/${crematorium?.id}/?utm_source=dogsound`}
                  imgSrc={
                    crematorium.images[0] ? crematorium.images[0].url : ""
                  }
                  name={
                    crematorium.companyname.includes("화장장") ||
                    crematorium.companyname.includes("화장터")
                      ? crematorium.companyname
                      : crematorium.companyname + " 화장장"
                  }
                  // mainColor={regionColor}

                  event={{
                    domain: "metro",
                    path: "crematorium",
                    trigger_and_target: "item_click",
                  }}
                />
              </li>
            ))}
          </VStack>
        </section>

        <CrematoriumRegionFAQ />
      </main>
    </>
  );
}

export default CrematoriumRegionSigunguPage;
